import React, { FC } from "react";
import { useTranslation } from "Providers/TranslationProvider";
import { useAuth } from "Components/Auth/AuthProvider";
import { useForm } from "react-hook-form";
import ErrorView from "Components/Layout/ErrorView";
import { useApp } from "Providers/AppProvider";
import {
  Card,
  makeStyles,
  Box,
  CardContent,
  CardActions,
  Button,
  TextField,
} from "@material-ui/core";
import { color, px } from "Configs/theme";
import Joi from "joi";
import { useRedirect } from "Helpers/RedirectHelper";
import { useSnackbar } from "material-ui-snackbar-provider";
// import {
//   useTracking,
//   TrackingPage,
// } from "Components/Tracking/TrackingProvider";
import { joiResolver } from "@hookform/resolvers";

interface FormValues {
  email: string;
  password: string;
}

const AuthSignInView: FC = () => {
  const classes = useStyles();
  const { isStandaloneApp } = useApp();
  const { t } = useTranslation();
  const auth = useAuth();
  const redirect = useRedirect();
  const snackbar = useSnackbar();
  // const { trackPage } = useTracking();

  // useEffect(() => {
  //   trackPage(TrackingPage.AUTH_SIGN_IN);
  // }, [trackPage]);

  const defaultValues: FormValues = {
    email: "",
    password: "",
  };

  const schema = Joi.object({
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "string.empty": t("Common_Form_Error_MandatoryField", {
          field: t("AuthSignInView_Email"),
        }),
      }),
    password: Joi.string()
      .required()
      .messages({
        "string.empty": t("Common_Form_Error_MandatoryField", {
          field: t("AuthSignInView_Password"),
        }),
      }),
  });

  async function onSubmit(formValues: FormValues) {
    try {
      await auth.signIn(formValues.email, formValues.password);
    } catch (error) {
      snackbar.showMessage(error.message);
    }
  }

  const form = useForm<FormValues>({
    resolver: joiResolver(schema),
    defaultValues,
  });

  const isDisabled = form.formState.isSubmitting;

  if (isStandaloneApp) {
    return (
      <form className={classes.form} onSubmit={form.handleSubmit(onSubmit)}>
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.title}>{t("AuthSignInView_Title")}</Box>
            <Box mt={2}>{t("AuthSignInView_Label")}</Box>
            <Box mt={3}>
              <TextField
                name="email"
                type="email"
                variant="outlined"
                label={t("AuthSignInView_Email")}
                inputRef={form.register}
                error={!!form.errors.email}
                helperText={form.errors.email?.message}
                disabled={isDisabled}
                autoFocus={true}
                fullWidth
              />
            </Box>
            <Box mt={3}>
              <TextField
                name="password"
                type="password"
                variant="outlined"
                label={t("AuthSignInView_Password")}
                inputRef={form.register}
                error={!!form.errors.password}
                helperText={form.errors.password?.message}
                disabled={isDisabled}
                fullWidth
              />
            </Box>
          </CardContent>
          <CardActions>
            <Button onClick={() => redirect("password-forgotten")}>
              {t("AuthSignInView_ForgotPassword")}
            </Button>
            <Box display="flex" flex={1} />
            <Button type="submit" color="primary" disabled={isDisabled}>
              {t("AuthSignInView_Login")}
            </Button>
          </CardActions>
        </Card>
      </form>
    );
  } else {
    return <ErrorView error={new Error(t("AuthSignInView_Error_LoggedOut"))} />;
  }
};

export default AuthSignInView;

const useStyles = makeStyles({
  form: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    backgroundColor: color.background,
    padding: px.xl,
  },
  card: {
    width: "100%",
    maxWidth: 460,
  },
  title: {
    textTransform: "uppercase",
    fontWeight: 800,
    letterSpacing: 0,
    fontSize: "16px",
  },
});
